import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, FlaskConical, Loader2, Pause, Play, Send, TriangleAlert, Upload } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  useCreateDeviceCommand,
  useDevice,
  useDeviceCommand,
  useDeviceCommands,
  useDeviceLogTail,
  useDeviceReadings,
  useFirmware,
  useOrgGenerators,
  useSetDeviceGenerator,
  useUpdateDevice,
  useUploadFirmware,
} from "@/api/hooks";
import { ApiError } from "@/api/problem";
import type {
  Device,
  DeviceCommand,
  DeviceCommandStatus,
  DeviceReading,
  GeneratorTelemetry,
} from "@/api/types";
import { useSession } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import { Dialog } from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  MicroLabel,
  Num,
} from "@/components/ui/primitives";
import { NONE, Select } from "@/components/ui/select";
import { EmptyState, ErrorState, FormError, LoadingBlock, LoadingRows } from "@/components/ui/states";
import { type ChartTone, TrendChart } from "@/components/ui/trend-chart";
import { useI18n } from "@/i18n/provider";
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
} from "@/lib/format";
import {
  ControlModeLabel,
  DataSourceBadge,
  dataSourceOf,
  deviceTitle,
  EngineChip,
  engineStateOf,
  GENERATOR_METRICS,
  LastSeen,
  MetricValue,
  OnlineBadge,
  shownGenerator,
  TANK_METRICS,
  useNow,
} from "./devices";

/**
 * One field board, end to end: what it reads right now, the last hour behind that, and — for
 * the people who run the boards — its log, its console, its name, its generator and the
 * firmware it can be told to install.
 *
 * Everything owner/admin-only is gated on the SAME permission the server enforces (see
 * `rbac.ts`). The log in particular names Wi-Fi networks and IP addresses; a viewer does not
 * get a panel that 403s, they get no panel.
 */

/* ------------------------------------------------------------------ header */

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="mb-8">
      <h2 id={id} className="mb-3 text-sm font-semibold text-text weight-display">
        {title}
      </h2>
      {children}
    </section>
  );
}

function MetaItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt>
        <MicroLabel>{label}</MicroLabel>
      </dt>
      <dd className="mt-0.5 truncate text-sm text-text">{children}</dd>
    </div>
  );
}

function DeviceMeta({ device }: { device: Device }) {
  const { t, lang } = useI18n();
  const now = useNow();
  const { generators } = useOrgGenerators({ enabled: !!device.generator_id });
  const linked = generators.find((g) => g.generator.id === device.generator_id);

  return (
    <Card className="mb-6">
      <CardBody>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <MetaItem label={t("devices.hardwareId")}>
            <span dir="ltr" className="font-mono text-xs">
              {device.hardware_id}
            </span>
          </MetaItem>
          <MetaItem label={t("devices.firmware")}>
            {device.fw_version ? <Num>v{device.fw_version}</Num> : t("common.none")}
          </MetaItem>
          <MetaItem label={t("devices.uptime")}>
            {device.uptime_s !== undefined ? (
              <Num>{formatDuration(lang, device.uptime_s)}</Num>
            ) : (
              t("common.none")
            )}
          </MetaItem>
          <MetaItem label={t("devices.lastSeenLabel")}>
            <LastSeen iso={device.last_seen_at} now={now} />
          </MetaItem>
          <MetaItem label={t("devices.signal")}>
            {device.wifi_rssi !== undefined ? (
              <Num>{formatNumber(lang, device.wifi_rssi)} dBm</Num>
            ) : (
              t("common.none")
            )}
          </MetaItem>
          <MetaItem label={t("devices.linkedGenerator")}>
            {device.generator_id ? (
              linked ? (
                `${linked.siteName} — ${linked.generator.name}`
              ) : (
                t("common.none")
              )
            ) : (
              <span className="text-faint">{t("devices.notLinked")}</span>
            )}
          </MetaItem>
        </dl>
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------------- tiles */

function Tile({ testId, label, children }: { testId: string; label: string; children: ReactNode }) {
  return (
    <Card data-testid={testId}>
      <CardBody className="flex flex-col gap-1.5">
        <MicroLabel>{label}</MicroLabel>
        <div className="text-lg">{children}</div>
      </CardBody>
    </Card>
  );
}

/* --------------------------------------------------------------- phases --
 *
 * A three-phase set is three separate supplies that happen to share an engine, and a fault
 * lives in the DIFFERENCE between them: one phase 40 V low, or carrying twice the current
 * of the other two, is the thing worth seeing. Three numbers in three tiles cannot be
 * compared; a table of rows and columns can be read down a column in one glance.
 *
 * `power_kw` is the set's total, not a fourth phase, so it sits in its own Total row.
 */
const PHASE_ROWS = [
  { key: "l1", voltage: "voltage_v", current: "current_a", power: "power_l1_kw" },
  { key: "l2", voltage: "voltage_l2_v", current: "current_l2_a", power: "power_l2_kw" },
  { key: "l3", voltage: "voltage_l3_v", current: "current_l3_a", power: "power_l3_kw" },
] as const;

const metricFor = (field: string) => GENERATOR_METRICS.find((m) => m.field === field)!;

function PhaseTable({ generator }: { generator: GeneratorTelemetry }) {
  const { t } = useI18n();

  const voltage = metricFor("voltage_v");
  const current = metricFor("current_a");
  const power = metricFor("power_kw");

  // A single-phase set, or a controller that exposes no per-phase registers, gets no table
  // at all — nine dashes teach nobody anything. One present value is enough to earn it.
  const anyPhaseValue = PHASE_ROWS.some(
    (row) =>
      generator[row.voltage] !== undefined ||
      generator[row.current] !== undefined ||
      generator[row.power] !== undefined,
  );
  if (!anyPhaseValue) return null;

  return (
    <Section id="generator-phases" title={t("devices.phases.title")}>
      <Card>
        <CardBody className="overflow-x-auto p-0">
          <table data-testid="phase-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule">
                <th scope="col" className="px-4 py-2 text-start">
                  <MicroLabel>{t("devices.phases.phase")}</MicroLabel>
                </th>
                <th scope="col" className="px-4 py-2 text-start">
                  <MicroLabel>{t("devices.metric.voltage")}</MicroLabel>
                </th>
                <th scope="col" className="px-4 py-2 text-start">
                  <MicroLabel>{t("devices.metric.current")}</MicroLabel>
                </th>
                <th scope="col" className="px-4 py-2 text-start">
                  <MicroLabel>{t("devices.metric.power")}</MicroLabel>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {PHASE_ROWS.map((row) => (
                <tr key={row.key} data-testid={`phase-row-${row.key}`}>
                  <th scope="row" className="px-4 py-2 text-start font-mono text-xs text-dim">
                    {row.key.toUpperCase()}
                  </th>
                  <td className="px-4 py-2">
                    <MetricValue value={generator[row.voltage]} metric={voltage} />
                  </td>
                  <td className="px-4 py-2">
                    <MetricValue value={generator[row.current]} metric={current} />
                  </td>
                  <td className="px-4 py-2">
                    <MetricValue value={generator[row.power]} metric={power} />
                  </td>
                </tr>
              ))}
              <tr data-testid="phase-row-total" className="bg-paper-sunk">
                <th scope="row" className="px-4 py-2 text-start text-xs font-medium text-text">
                  {t("devices.phases.total")}
                </th>
                {/* A sum of the three phase voltages, or of their currents, is not a
                    quantity — only the power adds up, so only the power is written. */}
                <td className="px-4 py-2" />
                <td className="px-4 py-2" />
                <td className="px-4 py-2">
                  <MetricValue value={generator.power_kw} metric={power} />
                </td>
              </tr>
            </tbody>
          </table>
        </CardBody>
      </Card>
    </Section>
  );
}

function LiveTiles({ reading }: { reading: DeviceReading | undefined }) {
  const { t } = useI18n();

  if (!reading) {
    return (
      <Section id="generator-now" title={t("devices.generatorSection")}>
        <Card>
          <EmptyState title={t("devices.noReading")} />
        </Card>
      </Section>
    );
  }

  // Empty when the controller did not answer: every tile then reads as a dash, which is the
  // truth, rather than the last values the board happened to have cached.
  const generator = shownGenerator(reading);
  const engine = engineStateOf(generator);
  const tank = reading.tank;
  const hasTank = !!tank && TANK_METRICS.some((m) => tank[m.field] !== undefined);

  return (
    <>
      <Section id="generator-now" title={t("devices.generatorSection")}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Tile testId="tile-running" label={t("devices.engine")}>
            <EngineChip state={engine} />
          </Tile>
          <Tile testId="tile-control_mode" label={t("devices.controlMode")}>
            <ControlModeLabel mode={generator.control_mode} />
          </Tile>
          {GENERATOR_METRICS.map((metric) => (
            <Tile key={metric.field} testId={`tile-${metric.field}`} label={t(metric.label)}>
              <MetricValue value={generator[metric.field]} metric={metric} />
            </Tile>
          ))}
        </div>
      </Section>

      <PhaseTable generator={generator} />

      {hasTank ? (
        <Section id="tank-now" title={t("devices.tankSection")}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {TANK_METRICS.map((metric) => (
              <Tile key={metric.field} testId={`tile-${metric.field}`} label={t(metric.label)}>
                <MetricValue value={tank[metric.field]} metric={metric} />
              </Tile>
            ))}
          </div>
        </Section>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ trends */

const CHARTED: { field: (typeof GENERATOR_METRICS)[number]["field"]; tone: ChartTone }[] = [
  { field: "power_kw", tone: "idle" },
  { field: "load_pct", tone: "idle" },
  { field: "engine_rpm", tone: "idle" },
  { field: "voltage_v", tone: "idle" },
  { field: "current_a", tone: "idle" },
  { field: "frequency_hz", tone: "idle" },
  { field: "coolant_temp_c", tone: "idle" },
  { field: "fuel_pct", tone: "ok" },
];

function DeviceTrends({ deviceId }: { deviceId: string }) {
  const { t, lang } = useI18n();
  const readingsQuery = useDeviceReadings(deviceId, 60);

  if (readingsQuery.isPending) {
    return (
      <Card>
        <LoadingRows rows={3} />
      </Card>
    );
  }
  if (readingsQuery.error) {
    return (
      <Card>
        <ErrorState error={readingsQuery.error} onRetry={() => void readingsQuery.refetch()} />
      </Card>
    );
  }

  const points = readingsQuery.data.points;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {CHARTED.map(({ field, tone }) => {
        const metric = GENERATOR_METRICS.find((m) => m.field === field)!;
        // Every reading in the window keeps its slot, with `null` where the controller gave
        // nothing. Dropping those rows would silently close the gap and draw a continuous
        // line across minutes in which nobody knew what the generator was doing.
        const series = points.map((p) => ({
          at: p.recorded_at,
          value: shownGenerator(p)[field] ?? null,
        }));
        const present = series.filter(
          (s): s is { at: string; value: number } => s.value !== null,
        );
        const first = present[0];
        const last = present[present.length - 1];
        const label = t(metric.label);

        return (
          <Card key={field}>
            <CardHeader className="py-2.5">
              <CardTitle>{label}</CardTitle>
              <MetricValue value={last?.value} metric={metric} className="text-sm" />
            </CardHeader>
            <CardBody className="flex flex-col gap-2">
              {first && last ? (
                <>
                  {/* Time runs left-to-right in either language — see TrendChart. */}
                  <div
                    dir="ltr"
                    role="img"
                    aria-label={`${label} — ${t("monitoring.chartSummary", {
                      from: formatNumber(lang, first.value, metric.digits),
                      to: formatNumber(lang, last.value, metric.digits),
                    })}`}
                    className="h-20 w-full"
                  >
                    <TrendChart
                      points={series.map((s) => ({ x: new Date(s.at).getTime(), y: s.value }))}
                      tone={tone}
                      height={80}
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-faint">
                    <Num>{formatDateTime(lang, first.at)}</Num>
                    <Num>{formatDateTime(lang, last.at)}</Num>
                  </div>
                </>
              ) : (
                <p className="py-6 text-center text-xs text-faint">{t("devices.noTrend")}</p>
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------- logs */

function LogsPanel({ deviceId }: { deviceId: string }) {
  const { t, lang } = useI18n();
  const [paused, setPaused] = useState(false);
  const tail = useDeviceLogTail(deviceId, { enabled: true, paused });
  const scrollRef = useRef<HTMLDivElement>(null);

  const lines = tail.data?.lines ?? [];

  // Follow the newest line unless the operator has paused to read.
  useEffect(() => {
    const el = scrollRef.current;
    if (!paused && el) el.scrollTop = el.scrollHeight;
  }, [tail.data, paused]);

  return (
    <Card className="mb-8">
      <CardHeader className="flex-wrap gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <CardTitle>{t("devices.logs.title")}</CardTitle>
          <p className="text-[11px] text-faint">{t("devices.logs.retention")}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-faint">
            {t(paused ? "devices.logs.paused" : "devices.logs.following")} ·{" "}
            <Num>{t("devices.logs.count", { count: formatNumber(lang, lines.length) })}</Num>
          </span>
          <Button variant="secondary" size="sm" onClick={() => setPaused((p) => !p)}>
            {paused ? <Play aria-hidden /> : <Pause aria-hidden />}
            {t(paused ? "devices.logs.resume" : "devices.logs.pause")}
          </Button>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {tail.error ? (
          <ErrorState error={tail.error} onRetry={() => void tail.refetch()} />
        ) : (
          <div
            ref={scrollRef}
            role="log"
            aria-live="off"
            aria-label={t("devices.logs.title")}
            dir="ltr"
            className="h-80 overflow-auto rounded-b-[6px] bg-well px-4 py-3 text-start font-mono text-xs leading-relaxed text-dim"
          >
            {tail.isPending ? (
              <LoadingRows rows={3} className="p-0" />
            ) : lines.length === 0 ? (
              <p className="text-faint">{t("devices.logs.empty")}</p>
            ) : (
              lines.map((line) => (
                <div key={line.seq} className="whitespace-pre-wrap break-all">
                  {line.line}
                </div>
              ))
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ----------------------------------------------------------------- console */

const QUICK_COMMANDS = ["status", "readings", "net", "wifi", "help"] as const;

/** Commands that act on physical equipment. Each one is confirmed before it is queued. */
const CONFIRMED_VERBS = { reboot: "devices.console.confirmReboot", update: "devices.console.confirmUpdate" } as const;

function confirmationFor(command: string) {
  const verb = command.split(/\s+/)[0]?.toLowerCase() ?? "";
  return verb in CONFIRMED_VERBS ? CONFIRMED_VERBS[verb as keyof typeof CONFIRMED_VERBS] : null;
}

const STATUS_TONE: Record<DeviceCommandStatus, "idle" | "ok"> = {
  queued: "idle",
  sent: "idle",
  done: "ok",
};

function CommandStatus({ status }: { status: DeviceCommandStatus }) {
  const { t } = useI18n();
  return <Badge tone={STATUS_TONE[status]}>{t(`devices.console.status.${status}` as const)}</Badge>;
}

function CommandOutput({ command }: { command: DeviceCommand }) {
  const { t } = useI18n();
  if (command.status !== "done") {
    return (
      <p className="flex items-center gap-2 text-xs text-faint">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        {t("devices.console.waiting")}
      </p>
    );
  }
  return (
    <pre
      dir="ltr"
      className="overflow-x-auto whitespace-pre-wrap break-all rounded-[6px] bg-well px-3 py-2 text-start font-mono text-xs leading-relaxed text-dim"
    >
      {command.output || t("devices.console.noOutput")}
    </pre>
  );
}

function ConsolePanel({ device }: { device: Device }) {
  const { t, lang } = useI18n();
  const [text, setText] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string>();

  const create = useCreateDeviceCommand(device.id);
  const active = useDeviceCommand(device.id, activeId);
  const recent = useDeviceCommands(device.id, { enabled: true });

  const dispatch = (command: string) => {
    create.mutate(command, {
      onSuccess: (queued) => {
        setActiveId(queued.id);
        setText("");
      },
    });
  };

  const send = (raw: string) => {
    const command = raw.trim();
    if (!command) return;
    if (confirmationFor(command)) setConfirming(command);
    else dispatch(command);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    send(text);
  };

  const confirmation = confirming ? confirmationFor(confirming) : null;
  const history = recent.data?.data ?? [];

  return (
    <Card className="mb-8">
      <CardHeader className="flex-wrap gap-1">
        <CardTitle>{t("devices.console.title")}</CardTitle>
        <p className="text-[11px] text-faint">{t("devices.console.hint")}</p>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {!device.online ? (
          <p role="status" className="rounded-[6px] bg-low-wash px-3 py-2 text-xs text-low">
            {t("devices.console.offline")}
          </p>
        ) : null}

        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <Field label={t("devices.console.label")}>
            {(field) => (
              <div className="flex gap-2">
                <Input
                  {...field}
                  dir="ltr"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={200}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("devices.console.placeholder")}
                  className="font-mono"
                />
                <Button type="submit" disabled={create.isPending || !text.trim()}>
                  <Send className="rtl:-scale-x-100" aria-hidden />
                  {t("devices.console.send")}
                </Button>
              </div>
            )}
          </Field>

          <div className="flex flex-wrap items-center gap-2">
            <MicroLabel>{t("devices.console.quick")}</MicroLabel>
            {QUICK_COMMANDS.map((command) => (
              <Button
                key={command}
                variant="secondary"
                size="sm"
                className="font-mono"
                disabled={create.isPending}
                onClick={() => send(command)}
              >
                {command}
              </Button>
            ))}
          </div>

          <FormError error={create.error} />
        </form>

        {active.data ? (
          <div data-testid="console-result" className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <code dir="ltr" className="font-mono text-sm text-text">
                &gt; {active.data.command}
              </code>
              <CommandStatus status={active.data.status} />
            </div>
            <CommandOutput command={active.data} />
          </div>
        ) : null}

        <div className="border-t border-rule pt-3">
          <h3 className="mb-2 text-xs font-semibold text-muted weight-display">
            {t("devices.console.recent")}
          </h3>
          {recent.isPending ? (
            <LoadingRows rows={2} className="p-0" />
          ) : recent.error ? (
            <ErrorState error={recent.error} onRetry={() => void recent.refetch()} />
          ) : history.length === 0 ? (
            <p className="text-xs text-faint">{t("devices.console.none")}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-rule">
              {history.map((command) => (
                <li key={command.id} className="py-1.5">
                  <details className="group">
                    <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 rounded-[4px] py-1 focus-visible:outline-2 focus-visible:outline-cream">
                      <code dir="ltr" className="font-mono text-xs text-text">
                        {command.command}
                      </code>
                      <span className="flex items-center gap-2 text-[11px] text-faint">
                        <Num>{formatDateTime(lang, command.created_at)}</Num>
                        {command.created_by ? (
                          <span>{t("devices.console.by", { name: command.created_by.name })}</span>
                        ) : null}
                        <CommandStatus status={command.status} />
                      </span>
                    </summary>
                    <div className="mt-2">
                      <CommandOutput command={command} />
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={t("devices.console.confirmTitle", { command: confirming ?? "" })}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (confirming) dispatch(confirming);
                setConfirming(null);
              }}
            >
              {t("devices.console.confirmSend")}
            </Button>
          </>
        }
      >
        {confirmation ? <p className="text-sm text-muted">{t(confirmation)}</p> : null}
      </Dialog>
    </Card>
  );
}

/* ------------------------------------------------------------------ manage */

function RenameForm({ device }: { device: Device }) {
  const { t } = useI18n();
  const [name, setName] = useState(device.name ?? "");
  const update = useUpdateDevice(device.id);

  const fieldErrors = update.error instanceof ApiError ? update.error.fieldErrors : {};
  const unchanged = name.trim() === (device.name ?? "");

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate({ name: name.trim() });
      }}
      className="flex flex-col gap-2"
    >
      <Field label={t("devices.manage.name")} hint={t("devices.manage.nameHint")} error={fieldErrors["name"]}>
        {(field) => (
          <div className="flex gap-2">
            <Input
              {...field}
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
              placeholder={device.hardware_id}
            />
            <Button type="submit" variant="secondary" disabled={update.isPending || unchanged || !name.trim()}>
              {update.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        )}
      </Field>
      {update.isSuccess && unchanged ? (
        <p role="status" className="text-xs text-ok">
          {t("devices.manage.saved")}
        </p>
      ) : null}
      {update.error && Object.keys(fieldErrors).length === 0 ? <FormError error={update.error} /> : null}
    </form>
  );
}

function ManageCard({ device }: { device: Device }) {
  const { t } = useI18n();
  const { generators, isPending } = useOrgGenerators({ enabled: true });
  const setGenerator = useSetDeviceGenerator(device.id);

  const options = [
    { value: NONE, label: t("devices.manage.noGenerator") },
    ...generators
      .filter(({ generator }) => generator.status !== "decommissioned")
      .map(({ generator, siteName }) => ({
        value: generator.id,
        label: `${siteName} — ${generator.name}`,
      })),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("devices.manage.title")}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-5">
        {/* Keyed by the saved name, so a rename from elsewhere resets the draft. */}
        <RenameForm key={device.name ?? ""} device={device} />

        <Field label={t("devices.manage.generator")} hint={t("devices.manage.generatorHint")}>
          {(field) => (
            <Select
              id={field.id}
              aria-describedby={field["aria-describedby"]}
              value={device.generator_id ?? NONE}
              disabled={isPending || setGenerator.isPending}
              onValueChange={(value) => setGenerator.mutate(value === NONE ? null : value)}
              options={options}
            />
          )}
        </Field>
        <FormError error={setGenerator.error} />
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- firmware */

function FirmwareCard({ device }: { device: Device }) {
  const { t, lang } = useI18n();
  const { can } = useSession();
  const firmware = useFirmware({ enabled: true });
  const upload = useUploadFirmware();
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);

  const image = firmware.data;

  return (
    <Card data-testid="firmware-card">
      <CardHeader>
        <CardTitle>{t("devices.firmware.title")}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {firmware.isPending ? (
          <LoadingRows rows={2} className="p-0" />
        ) : firmware.error ? (
          <ErrorState error={firmware.error} onRetry={() => void firmware.refetch()} />
        ) : !image ? (
          <p className="text-sm text-muted">{t("devices.firmware.none")}</p>
        ) : (
          <dl className="grid grid-cols-2 gap-3">
            <MetaItem label={t("devices.firmware.version")}>
              {image.version ? <Num>v{image.version}</Num> : t("devices.firmware.untagged")}
            </MetaItem>
            <MetaItem label={t("devices.firmware.size")}>
              <Num>{formatBytes(lang, image.size_bytes)}</Num>
            </MetaItem>
            <div className="col-span-2 min-w-0">
              <dt>
                <MicroLabel>SHA-256</MicroLabel>
              </dt>
              <dd dir="ltr" className="mt-0.5 break-all text-start font-mono text-xs text-dim">
                {image.sha256}
              </dd>
            </div>
            <div className="col-span-2 min-w-0">
              <dt>
                <MicroLabel>{t("devices.firmware.uploaded")}</MicroLabel>
              </dt>
              <dd className="mt-0.5 text-xs text-dim">
                <Num>{formatDateTime(lang, image.created_at)}</Num>
                {image.uploaded_by ? ` · ${image.uploaded_by.name}` : null}
              </dd>
            </div>
          </dl>
        )}

        {device.fw_version ? (
          <p className="text-xs text-faint">
            {t("devices.firmware.boardRuns", { version: `v${device.fw_version}` })}
          </p>
        ) : null}

        {can("firmware:upload") ? (
          <form
            className="flex flex-col gap-3 border-t border-rule pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!file) return;
              upload.mutate(file, {
                onSuccess: () => {
                  setFile(null);
                  setInputKey((k) => k + 1);
                },
              });
            }}
          >
            <Field label={t("devices.firmware.file")} hint={t("devices.firmware.uploadHint")}>
              {(field) => (
                <Input
                  key={inputKey}
                  {...field}
                  type="file"
                  accept=".bin,application/octet-stream"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="h-auto py-2 file:me-3 file:rounded-[6px] file:border-0 file:bg-paper file:px-2 file:py-1 file:text-sm file:text-text"
                />
              )}
            </Field>
            <FormError error={upload.error} />
            <div>
              <Button type="submit" variant="secondary" disabled={!file || upload.isPending}>
                <Upload aria-hidden />
                {upload.isPending ? t("devices.firmware.uploading") : t("devices.firmware.upload")}
              </Button>
            </div>
          </form>
        ) : (
          <p className="border-t border-rule pt-4 text-xs text-faint">{t("devices.firmware.ownerOnly")}</p>
        )}
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------- page */

export function DeviceDetailPage() {
  const { t } = useI18n();
  // The id comes from the path wherever this screen is mounted — see SiteDetailPage.
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };
  const { can } = useSession();
  const deviceQuery = useDevice(deviceId);

  const back = (
    <Link
      to="/devices"
      className="mb-3 inline-flex items-center gap-1.5 rounded-[4px] text-sm text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-cream"
    >
      <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden />
      {t("devices.back")}
    </Link>
  );

  if (deviceQuery.isPending) return <LoadingBlock />;
  if (deviceQuery.error) {
    return (
      <>
        {back}
        <ErrorState error={deviceQuery.error} onRetry={() => void deviceQuery.refetch()} />
      </>
    );
  }

  const device = deviceQuery.data;
  const reading = device.latest_reading;
  const source = dataSourceOf(reading);

  return (
    <>
      {back}
      <PageHeader
        title={deviceTitle(device)}
        description={device.name ? device.hardware_id : undefined}
        actions={
          <>
            <DataSourceBadge source={source} />
            <OnlineBadge online={device.online} />
          </>
        }
      />

      {source === "simulated" ? (
        <p
          role="note"
          className="mb-4 flex items-center gap-2 rounded-[6px] border border-rule-strong bg-ink px-3 py-2 text-xs text-cream"
        >
          <FlaskConical className="size-3.5 shrink-0" aria-hidden />
          {t("devices.simulatedHint")}
        </p>
      ) : null}

      {/* A board that is up while its controller is not is a FAULT, and the loudest thing
          on this screen — the tiles below it are all dashes, and this says why. */}
      {source === "controller-offline" ? (
        <p
          role="status"
          data-testid="controller-offline-note"
          className="mb-4 flex items-start gap-2 rounded-[6px] bg-dry-wash px-3 py-2 text-xs text-dry"
        >
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-medium">{t("devices.controllerOffline")}</span>{" "}
            {t("devices.controllerOfflineHint")}
          </span>
        </p>
      ) : null}

      <DeviceMeta device={device} />

      <LiveTiles reading={reading} />

      <Section id="device-trends" title={t("devices.trendsSection")}>
        <DeviceTrends deviceId={device.id} />
      </Section>

      {can("devices:logs") ? <LogsPanel deviceId={device.id} /> : null}

      {can("devices:manage") ? <ConsolePanel device={device} /> : null}

      {can("devices:manage") || can("firmware:read") ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {can("devices:manage") ? <ManageCard device={device} /> : null}
          {can("firmware:read") ? <FirmwareCard device={device} /> : null}
        </div>
      ) : null}
    </>
  );
}
